import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Search, BookOpen, Award, ArrowLeft, Headphones } from 'lucide-react';
import { useAuth, isAdmin } from '@/lib/auth';
import { api } from '@/lib/api';
import { useCourseLang, applyUrlLang } from '@/lib/courseLang';
import SearchBar from '@/components/SearchBar';

// Learner shell — single locked Ultratech look (no light/dark toggle).
// Clean light body with a dark brand header bar (#2C2300, rgb 44/35/0).
// Tabs — Explore, Topics, Certificates, Call us.
const TABS = [
    { to: '/learn', label: 'Explore', icon: Search, exact: true, testid: 'tab-feeds' },
    { to: '/learn/courses', label: 'Topics', icon: BookOpen, testid: 'tab-courses' },
    { to: '/learn/certificates', label: 'Certificates', icon: Award, testid: 'tab-certificates' },
];

// "Call us" → opens the brand support / helpline number.
const CALL_US_HREF = 'tel:18001213355';

// Dark brand header colour — green theme base (doc Point 62).
const HEADER_BG = '#013022';
const PAGE_BG = '#02160E';
const BODY_BG = '#02160E';

// Arcade pages get their own immersive dark-green theme (per the Arcade Colour Guide) with a
// slim header and NO footer / NO search — kept visually separate from the LMS course shell
// so games get more screen area (doc Points 28, 29, 30).
const ARCADE_PAGE_BG = 'radial-gradient(1100px 560px at 50% -8%, #075437 0%, #02160E 55%, #000000 100%)';
const ARCADE_HEADER_BG = 'linear-gradient(180deg, #013022 0%, #011c14 100%)';

const TAB_ACTIVE = 'border-brand-yellow text-brand-yellow';
const TAB_INACTIVE = 'border-transparent text-white/55 hover:text-white';

// Native language labels — used so the header dropdown can render the selected language
// (e.g. the one passed on the Loyalty SSO deeplink) even before the course-languages list
// has loaded (doc Point 29).
const LANG_LABEL = {
    en: 'English', hi: 'हिंदी', ta: 'தமிழ்', te: 'తెలుగు', gu: 'ગુજરાતી',
    bn: 'বাংলা', kn: 'ಕನ್ನಡ', ml: 'മലയാളം', mr: 'मराठी', or: 'ଓଡ଼ିଆ',
};

// Course-language selector (header, before the search bar). Filters the courses shown on
// Explore / Topics to the chosen language. Lists only languages that have published courses.
function CourseLangSelect() {
    const [lang, setLang] = useCourseLang();
    const [langs, setLangs] = useState([]);
    useEffect(() => {
        api.get('/trainee/course-languages').then((r) => setLangs(r.data || [])).catch(() => setLangs([]));
    }, []);
    // Always render the currently-selected language (e.g. the SSO/Loyalty deeplink language)
    // from the first paint — even before the course-languages list resolves — so the dropdown
    // never flashes or sticks on English (doc Point 29).
    const base = langs.length ? langs : [];
    const options = base.some((o) => o.code === lang)
        ? base
        : [{ code: lang, label: LANG_LABEL[lang] || lang.toUpperCase() }, ...base];
    return (
        <select
            data-testid="course-lang-select"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            aria-label="Course language"
            className="rounded-lg bg-white/90 text-slate-800 text-xs font-bold px-2 py-1.5 ring-1 ring-white/30 focus:outline-none max-w-[130px]"
        >
            {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
    );
}

export default function TraineeLayout() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const isArcade = pathname.startsWith('/learn/arcade');

    // Doc Point 63: adopt the `?lang=<code>` passed from the Loyalty app (default en).
    useEffect(() => { applyUrlLang(); }, []);

    // Back arrow → returns to the host Loyalty mobile app home screen.
    const onBack = () => { window.location.href = process.env.REACT_APP_LOYALTY_HOME_URL; };

    return (
        <div
            className={`learner-theme relative min-h-screen ${isArcade ? 'pb-0' : 'pb-24 lg:pb-0'}`}
            style={{ background: isArcade ? ARCADE_PAGE_BG : PAGE_BG }}
            data-testid="trainee-layout"
        >
            <header
                className="relative z-10 overflow-hidden"
                style={isArcade
                    ? { backgroundImage: ARCADE_HEADER_BG }
                    : { backgroundColor: HEADER_BG, backgroundImage: 'url(/header-green.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
                {/* Dark wash keeps the back arrow, search and logo legible over the header artwork */}
                {!isArcade && <div className="absolute inset-0 bg-black/25 pointer-events-none" />}
                <div className={`relative max-w-6xl mx-auto px-4 sm:px-6 pt-3 flex items-center justify-between gap-2 ${isArcade ? 'pb-3' : 'pb-9 lg:pb-3'}`}>
                    <button
                        onClick={onBack}
                        data-testid="trainee-back-btn"
                        aria-label="Back to Loyalty app"
                        className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition text-white"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>

                    <div className="flex items-center gap-2">
                        <CourseLangSelect />
                        {/* Search is hidden on Arcade screens (doc Point 30) */}
                        {!isArcade && <SearchBar />}
                        {isAdmin(user) && (
                            <Link
                                to="/admin"
                                className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full bg-brand-yellow text-brand-black text-xs font-bold hover:bg-brand-yellowDark transition"
                                data-testid="trainee-switch-admin"
                            >
                                Admin
                            </Link>
                        )}
                        <Link to="/learn" data-testid="trainee-logo-link" className="ml-0.5 flex-shrink-0">
                            <img
                                src="/propulse-logo.jpeg"
                                alt="Aditya Birla Electricals"
                                data-testid="trainee-logo"
                                style={{ width: '30px', height: '34.5px', objectFit: 'contain' }}
                            />
                        </Link>
                    </div>
                </div>
                {/* Desktop tabs — hidden on Arcade (doc Point 29 / immersive header) */}
                {!isArcade && (
                <div className="relative hidden lg:block border-t border-white/10">
                    <div className="max-w-6xl mx-auto px-6">
                        <nav className="flex gap-1 -mb-px">
                            {TABS.map((t) => (
                                <NavLink
                                    key={t.to} to={t.to} end={t.exact} data-testid={`desktop-${t.testid}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2 px-4 py-3 text-sm font-bold tracking-tight border-b-2 transition ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
                                >
                                    <t.icon className="h-5 w-5" /> {t.label}
                                </NavLink>
                            ))}
                            <a
                                href={CALL_US_HREF} data-testid="desktop-tab-call-us"
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold tracking-tight border-b-2 ${TAB_INACTIVE}`}
                            >
                                <Headphones className="h-5 w-5" /> Call us
                            </a>
                        </nav>
                    </div>
                </div>
                )}
            </header>

            {/* Body. LMS pages curve into the header (rounded brown card); Arcade is a flush
                immersive green canvas with more room and no footer. */}
            <main
                className={isArcade
                    ? 'relative z-20 max-w-6xl mx-auto px-3 sm:px-6 pt-4 pb-8 min-h-[calc(100vh-90px)]'
                    : 'relative z-20 -mt-7 lg:mt-0 rounded-t-[28px] lg:rounded-t-none max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-6 lg:py-8 min-h-[calc(100vh-150px)]'}
                style={isArcade ? undefined : { background: BODY_BG }}
                data-testid="trainee-content"
            >
                <Outlet />
            </main>

            {/* Mobile docked bottom nav — hidden on Arcade screens (doc Point 29) */}
            {!isArcade && (
            <nav
                className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_24px_rgba(0,0,0,0.25)] px-2 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] flex justify-between items-center"
                data-testid="trainee-bottom-nav"
            >
                {TABS.map((t) => (
                    <NavLink
                        key={t.to} to={t.to} end={t.exact} data-testid={`mobile-${t.testid}`}
                        className="flex flex-col items-center justify-center flex-1 h-14 transition-colors"
                    >
                        {({ isActive }) => (
                            <>
                                {isActive ? (
                                    // Stack two layers: a yellow-filled base + a stroke-only overlay
                                    // so inner details (e.g. the BookOpen spine) stay visible on the fill.
                                    <span className="relative inline-flex h-8 w-8">
                                        <t.icon className="absolute inset-0 h-8 w-8 fill-brand-yellow text-brand-yellow" strokeWidth={2.4} />
                                        <t.icon className="absolute inset-0 h-8 w-8 text-slate-700" strokeWidth={2.4} />
                                    </span>
                                ) : (
                                    <t.icon className="h-7 w-7 text-slate-400" strokeWidth={2} />
                                )}
                                <span className={`text-xs mt-1 leading-none ${isActive ? 'text-brand-black font-bold' : 'text-slate-400 font-medium'}`}>{t.label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
                <a
                    href={CALL_US_HREF} data-testid="mobile-tab-call-us"
                    className="flex flex-col items-center justify-center flex-1 h-14 transition-colors"
                >
                    <Headphones className="h-7 w-7 text-slate-400" strokeWidth={2} />
                    <span className="text-xs mt-1 leading-none text-slate-400 font-medium">Call us</span>
                </a>
            </nav>
            )}
        </div>
    );
}
